import { useEffect, useState } from "react";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import FormField from "../../../components/common/FormField";
import DraftNotice from "../../../components/common/DraftNotice";
import useFormDraft from "../../../hooks/useFormDraft";
import { EMPLOYMENT_STATUSES } from "../../../utils/constants";
import { ROLE_LABELS } from "../../../services/navigation";
import { validateEmployee, hasErrors } from "../../../utils/validators";
import RoleCheckboxes, { roleListLabel } from "../../admin/components/RoleCheckboxes";
import Select from "../../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)
import LocationPicker from "../../../components/common/LocationPicker";

const EMPTY = {
  id: "", name: "", email: "", departmentId: "", designationId: "", managerId: "",
  dateOfJoining: "", location: "", status: "Active", role: "employee",
  // [NEW] migration 010 HR attributes (optional)
  dateOfBirth: "", gender: "", mobilePhone: "", personalEmail: "", currentAddress: "",
  legalEntity: "", businessUnit: "", costCenter: "", primarySkill: "", professionalExperience: "",
};

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];

export default function EmployeeFormModal({ open, employee, employees = [], lookups, canEditRoles = false, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(employee);
  // [NEW] existing cost centers, offered as suggestions in the Cost center field
  const costCenters = [...new Set(employees.map((e) => e.costCenter).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (open) {
      setForm(employee
        ? { ...EMPTY, ...employee, managerId: employee.managerId || "", roleKeys: employee.roles?.length ? employee.roles : ["employee"], rolesTouched: false }
        : EMPTY);
      setErrors({});
    }
  }, [open, employee]);

  // [NEW] Keep unsaved employee details (add or edit) in this browser until saved.
  const draft = useFormDraft(`employee:${employee?.id || "new"}`, form, setForm, { active: open });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    const found = validateEmployee(form, employees, isEdit);
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    // A new employee is always created as Active, whatever an old saved draft contains.
    const ok = await onSave(isEdit ? form : { ...form, status: "Active" });
    setSaving(false);
    if (ok) { draft.clear(); onClose(); }
  };

  const managers = (lookups?.managers || []).filter((m) => m.id !== form.id);

  return (
    <Modal
      open={open}
      size="lg"
      title={isEdit ? `Edit ${employee?.name}` : "Add employee"}
      subtitle="Employee ID and official email must be unique."
      onClose={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>{isEdit ? "Save changes" : "Add employee"}</Button>
        </>
      }
    >
      <DraftNotice draft={draft} />
      <div className="form-grid">
        <FormField label="Employee ID" required error={errors.id}>
          <input value={form.id} onChange={set("id")} disabled={isEdit} placeholder="E1015" />
        </FormField>
        <FormField label="Full name" required error={errors.name}>
          <input value={form.name} onChange={set("name")} />
        </FormField>
        <FormField label="Official email" required error={errors.email}>
          <input type="email" value={form.email} onChange={set("email")} placeholder="name@teliolabs.com" />
        </FormField>
        <FormField label="Department" required error={errors.departmentId}>
          <Select value={form.departmentId} onChange={set("departmentId")}>
            <option value="">Choose a department</option>
            {(lookups?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </FormField>
        <FormField label="Designation" required error={errors.designationId}>
          <Select value={form.designationId} onChange={set("designationId")}>
            <option value="">Choose a designation</option>
            {(lookups?.designations || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </FormField>
        <FormField label="Reporting manager" error={errors.managerId} hint="Leave empty for the top of the hierarchy.">
          <Select value={form.managerId} onChange={set("managerId")}>
            <option value="">No manager</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
          </Select>
        </FormField>
        <FormField label="Date of joining" required error={errors.dateOfJoining}>
          <input type="date" value={form.dateOfJoining} onChange={set("dateOfJoining")} />
        </FormField>
        <FormField label="Location">
          <LocationPicker
            value={form.location}
            onChange={(v) => setForm((f) => ({ ...f, location: v }))}
          />
        </FormField>
        {/* Employment status is shown only when editing. New employees are always created as Active. */}
        {isEdit && (
          <FormField label="Employment status">
            <Select value={form.status} onChange={set("status")}>
              {EMPLOYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
        )}
        {/* [NEW] HR details from backend migration 010. All optional; sent as null when empty. */}
        <div className="span-2 form-section-title">Additional details <span className="muted">(optional)</span></div>
        <FormField label="Date of birth" error={errors.dateOfBirth}>
          <input type="date" value={form.dateOfBirth} onChange={set("dateOfBirth")} />
        </FormField>
        <FormField label="Gender" error={errors.gender}>
          <Select value={form.gender} onChange={set("gender")}>
            <option value="">Not specified</option>
            {[...new Set([...GENDER_OPTIONS, form.gender].filter(Boolean))].map((g) => <option key={g} value={g}>{g}</option>)}
          </Select>
        </FormField>
        <FormField label="Mobile phone" error={errors.mobilePhone}>
          <input type="tel" value={form.mobilePhone} onChange={set("mobilePhone")} maxLength={50} placeholder="+91 98765 43210" />
        </FormField>
        <FormField label="Personal email" error={errors.personalEmail}>
          <input type="email" value={form.personalEmail} onChange={set("personalEmail")} maxLength={255} placeholder="name@gmail.com" />
        </FormField>
        <FormField label="Legal entity" error={errors.legalEntity}>
          <input value={form.legalEntity} onChange={set("legalEntity")} maxLength={200} />
        </FormField>
        <FormField label="Business unit" error={errors.businessUnit}>
          <input value={form.businessUnit} onChange={set("businessUnit")} maxLength={200} />
        </FormField>
        <FormField label="Cost center" error={errors.costCenter} hint="Pick an existing one or type a new one, e.g. TTBS.">
          <input value={form.costCenter} onChange={set("costCenter")} maxLength={100} list="pms-cost-centers" placeholder="e.g. TTBS" />
          <datalist id="pms-cost-centers">
            {(costCenters || []).map((c) => <option key={c} value={c} />)}
          </datalist>
        </FormField>
        <FormField label="Primary skill" error={errors.primarySkill}>
          <input value={form.primarySkill} onChange={set("primarySkill")} maxLength={500} placeholder="e.g. React, Java, Sales" />
        </FormField>
        <FormField label="Professional experience" error={errors.professionalExperience}>
          <input value={form.professionalExperience} onChange={set("professionalExperience")} maxLength={100} placeholder="e.g. 5 years" />
        </FormField>
        <FormField label="Current address" error={errors.currentAddress} className="span-2">
          <textarea rows={2} value={form.currentAddress} onChange={set("currentAddress")} maxLength={500} />
        </FormField>
        {!isEdit && (
          <FormField label="Application role" required error={errors.role} className="span-2" hint="Controls which screens this person can open. The Employee role is always added too.">
            <Select value={form.role} onChange={set("role")}>
              {Object.entries(ROLE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </Select>
          </FormField>
        )}
        {isEdit && canEditRoles && employee?.rolesKnown && (
          <FormField label="Application roles" className="span-2" hint="Tick or untick single roles; the other roles are kept.">
            <RoleCheckboxes
              value={form.roleKeys || ["employee"]}
              directReportCount={employee?.directReportCount || 0}
              disabled={form.status !== "Active"}
              onChange={(roleKeys) => setForm((f) => ({ ...f, roleKeys, rolesTouched: true }))}
            />
          </FormField>
        )}
        {isEdit && !(canEditRoles && employee?.rolesKnown) && (
          <FormField label="Application roles" className="span-2" hint="Only an administrator can change roles (Users & roles).">
            <input value={employee?.rolesKnown ? roleListLabel(employee.roles) : "Visible to administrators only"} disabled />
          </FormField>
        )}
      </div>
    </Modal>
  );
}